import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import cx from 'classnames';
import { Help, Popup, Table, TableHead, TableBody, TableCell, Spin, Text, Switch } from 'platform-components';
import {
    HELP_ACTION,
    HELP_THEME,
    POPUP_AXIS,
    POPUP_DIRECTIONS,
    POPUP_THEMES,
    HELP_TYPE,
} from 'platform-components/constants';

import { Student } from 'common.components/typings';
import { getCanEditClass, getIsCurrentClassArchived } from 'common.components/user/selectors';
import { ReduxUserStore } from 'common.components/user/typings';

import { StudentTableContent } from '../__table-content/students__table-content';

import './students__table.scss';

const helpProps = {
    mix: 'students__table-help',
    popupProps: {
        theme: POPUP_THEMES.SCHOOL,
        direction: POPUP_DIRECTIONS.RIGHT,
        hasArrow: true,
    },
    showPopupOnAction: HELP_ACTION.HOVER,
    theme: HELP_THEME.SCHOOL,
    type: HELP_TYPE.QUESTION_SMALL,
};

const popupProps = {
    autoclosable: true,
    axis: POPUP_AXIS.START,
    direction: POPUP_DIRECTIONS.LEFT,
    hasArrow: true,
    mix: 'students__actual-popup',
    offsetTop: 104,
    theme: POPUP_THEMES.SCHOOL,
};

interface Props {
    children?: React.ReactNode;
    code: string;
    teamId: number;
    loadingStatus: LOADING_STATUSES;
    mix?: string;
    onSwitch: () => void;
    shouldShowAccountSwitchControl: boolean;
    shouldShowActualizationPopup?: boolean;
    showPassportAccountsInClass: boolean;
    students: Student[] | null;
}

export const StudentsTable = (props: Props) => {
    const {
        children,
        teamId,
        code,
        shouldShowAccountSwitchControl,
        loadingStatus,
        mix,
        shouldShowActualizationPopup,
        showPassportAccountsInClass,
        onSwitch,
        students,
    } = props;

    const canEditClass = useSelector<ReduxUserStore, boolean>((storeState) => getCanEditClass(storeState, teamId));
    const isArchiveClass = useSelector<ReduxUserStore, boolean>((storeState) =>
        getIsCurrentClassArchived(storeState, { classId: teamId })
    );

    const popupRef = useRef<Popup | null>(null);

    const isEmpty = !students?.length;

    const tableMix = cx(b('students', { empty: isEmpty }), mix);

    useEffect(() => {
        if (shouldShowActualizationPopup && popupRef.current) {
            popupRef.current.toggle();
        }
    });

    return (
        <Table mix={tableMix}>
            <TableHead mix="students__table-head">
                <TableCell mix="students__table-student-counter">
                    <Text id="class.table.field.number" />
                </TableCell>
                <TableCell mix="students__table-student-name">
                    <Text id="class.table.field.students" />
                </TableCell>
                {showPassportAccountsInClass ? (
                    <TableCell mix="students__table-student-passport-login-password">
                        <Text id="class.table.field.loginAndPassword" />
                    </TableCell>
                ) : (
                    <>
                        <TableCell mix="students__table-student-login">
                            <Text id="class.table.field.login" />
                            <Help {...helpProps}>
                                <Text id="class.table.access.popup" />
                            </Help>
                        </TableCell>
                        <TableCell mix="students__table-school-code">
                            <Text id="class.table.field.schoolCode" />
                            <Help {...helpProps}>
                                <Text id="class.table.parents.popup" />
                            </Help>
                        </TableCell>
                    </>
                )}
                {shouldShowAccountSwitchControl && (
                    <TableCell mix="students__table-view-switch">
                        <Switch
                            checked={showPassportAccountsInClass}
                            labelPosition={Switch.LABEL_POSITIONS.LEFT}
                            onChange={onSwitch}
                            theme={Switch.THEMES.LIGHT_BLUE}
                            view={Switch.VIEWS.INVERSE}
                        />
                    </TableCell>
                )}
            </TableHead>
            <TableBody mix="students__table-body">
                {loadingStatus === LOADING_STATUSES.LOADING ? (
                    <Spin center />
                ) : (
                    <StudentTableContent
                        canEditClass={canEditClass}
                        code={code}
                        isArchiveClass={isArchiveClass}
                        shouldShowPassportAccounts={showPassportAccountsInClass}
                        students={students}
                        teamId={teamId}
                    />
                )}
                {children}
                <Popup {...popupProps} ref={popupRef}>
                    <Text id="class.renewClass.header" />
                    <div className="students__renew-class-description">
                        <Text id="class.renewClass.description" />
                    </div>
                </Popup>
            </TableBody>
        </Table>
    );
};
